import { useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { enPathFor, frPathFor } from "../seo/enRoutes";

const BASE_URL = "https://doodates.com";

interface SEOHeadProps {
  /** Titre de la page (sans "— DooDates", ajouté automatiquement) */
  title?: string;
  description?: string;
  /** Forcer noindex (ex : pages admin, pages dynamiques de sondage) */
  noIndex?: boolean;
  /** `title` est déjà complet : ne pas ajouter le suffixe « — DooDates ». */
  fullTitle?: boolean;
  /**
   * Données structurées schema.org propres à la page. Par défaut, un
   * WebPage (titre, description, URL). `false` = aucun (la page d'accueil
   * garde le JSON-LD statique d'index.html).
   */
  jsonLd?: Record<string, unknown> | false;
}

/** Sérialise un JSON-LD sans permettre de fermer la balise <script>. */
const serializeJsonLd = (data: Record<string, unknown>) =>
  JSON.stringify(data).replace(/</g, "\\u003c");

/**
 * Injecte le <title>, la meta description, le canonical et le robots
 * via react-helmet-async pour chaque route.
 *
 * - Le canonical est calculé automatiquement depuis l'URL courante.
 * - Sur un domaine *.vercel.app (previews Vercel), noindex est forcé
 *   pour éviter d'indexer les environnements de déploiement.
 */
export function SEOHead({
  title,
  description,
  noIndex = false,
  fullTitle: titleIsFull = false,
  jsonLd,
}: SEOHeadProps) {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const canonical =
    pathname === "/" ? `${BASE_URL}/` : `${BASE_URL}${pathname}`;
  const path = pathname === "/" ? "" : pathname;
  // Page ayant une version /en/ (ou étant elle-même cette version) :
  // hreflang réciproques FR ↔ /en/, x-default sur la version /en/.
  const frPath = frPathFor(pathname) ?? (enPathFor(pathname) ? pathname : null);
  const enPath = frPath ? enPathFor(frPath) : null;
  const frUrl = frPath
    ? frPath === "/"
      ? `${BASE_URL}/`
      : `${BASE_URL}${frPath}`
    : null;
  const alternates: [string, string][] =
    frUrl && enPath
      ? [
          ["fr", frUrl],
          ["en", `${BASE_URL}${enPath}`],
          ["es", `${frUrl}?lang=es`],
          // x-default : la version anglaise, servie aux langues non supportées.
          ["x-default", `${BASE_URL}${enPath}`],
        ]
      : [
          ["fr", `${BASE_URL}${path}?lang=fr`],
          ["en", `${BASE_URL}${path}?lang=en`],
          ["es", `${BASE_URL}${path}?lang=es`],
          ["x-default", `${BASE_URL}${path}`],
        ];
  const ogLocale = lang === "en" ? "en_US" : lang === "es" ? "es_ES" : "fr_FR";

  const isVercelPreview =
    typeof window !== "undefined" &&
    window.location.hostname.endsWith(".vercel.app");

  const shouldNoIndex = noIndex || isVercelPreview;

  // Force recalculation when language changes, even if title prop doesn't
  const fullTitle = useMemo(() => {
    if (!title) return t("seo_default_title");
    return titleIsFull ? title : `${title} — DooDates`;
  }, [title, titleIsFull, i18n.language]);

  const metaDescription = useMemo(() => {
    return description ?? t("seo_default_description");
  }, [description, i18n.language]);

  // Filet de sécurité pour le titre + sync des metas que Helmet ne réconcilie
  // pas toujours lors d'un switch de langue client-side.
  // NOTE : le MutationObserver de dynamic-title.ts qui restaurait l'ancien
  // titre sur "/" a été supprimé — SEOHead est l'unique propriétaire du titre.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = fullTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", metaDescription);
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", fullTitle);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", metaDescription);
    }
  }, [fullTitle, metaDescription, i18n.language]);

  const structuredData =
    jsonLd === false
      ? null
      : (jsonLd ?? {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: fullTitle,
          description: metaDescription,
          url: canonical,
          inLanguage: lang,
          isPartOf: {
            "@type": "WebSite",
            name: "DooDates",
            url: `${BASE_URL}/`,
          },
        });

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={shouldNoIndex ? "noindex,nofollow" : "index,follow"}
      />
      {/* hreflang — variantes de langue */}
      {alternates.map(([hrefLang, href]) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      {/* Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {structuredData && (
        <script type="application/ld+json">
          {serializeJsonLd(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
